import { Fragment, createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { formatDateLocale, formatMoneyLocale, isUiLanguage, localeFor, normalizeLanguage, type LanguageCode, type UiLanguage } from "@causetide/shared";
import { fmtMoneyIn } from "../api.js";
import { en, type Catalog, type MessageKey } from "./en.js";
import { es } from "./es.js";

/**
 * CT-RM-30: the app's i18n framework — string catalogs (en is the default and
 * source of truth, es is human-written for public surfaces), {placeholder}
 * interpolation, plural keys, and locale-aware money/date formatting. No
 * runtime dependency, no machine translation.
 */

export type { MessageKey } from "./en.js";

export const CATALOGS: Record<UiLanguage, Catalog> = { en, es };

type Vars = Record<string, string | number>;

const pluralRules = new Map<string, Intl.PluralRules>();
function pluralKey(lang: UiLanguage, key: string, count: number): string {
  let rules = pluralRules.get(lang);
  if (!rules) pluralRules.set(lang, (rules = new Intl.PluralRules(lang)));
  return `${key}.${rules.select(count) === "one" ? "one" : "other"}`;
}

function lookup(lang: UiLanguage, key: string): string | undefined {
  return (CATALOGS[lang] as Record<string, string | undefined>)[key] ?? (en as Record<string, string>)[key];
}

/** catalog lookup with English fallback, {var} interpolation and .one/.other plurals */
export function translate(language: string | null | undefined, key: MessageKey | string, vars?: Vars): string {
  const lang: UiLanguage = isUiLanguage(normalizeLanguage(language)) ? (normalizeLanguage(language) as UiLanguage) : "en";
  const count = vars?.count;
  const template = (typeof count === "number" ? lookup(lang, pluralKey(lang, key, count)) : undefined) ?? lookup(lang, key) ?? key;
  return vars ? template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : template;
}

export interface I18n {
  /** the content language (may have no UI catalog, e.g. fr) */
  language: LanguageCode;
  /** the catalog in use for chrome: language when it has one, else English */
  uiLanguage: UiLanguage;
  t: (key: MessageKey, vars?: Vars) => string;
  /** like t, but placeholders may be React nodes (links, <strong>) */
  rich: (key: MessageKey, nodes: Record<string, ReactNode>) => ReactNode;
  /** minor units → "$50.00" (en house format) / "50,00 €" (locale) */
  money: (minor: number, currency: string) => string;
  /** ISO date/timestamp → "Nov 4, 2026" / "4 nov 2026" */
  date: (iso: string, style?: "long" | "medium" | "short") => string;
  /** BCP 47 locale for Intl calls */
  locale: string;
}

export function makeI18n(language: string | null | undefined, currency?: string | null): I18n {
  const lang = normalizeLanguage(language) ?? "en";
  const uiLanguage: UiLanguage = isUiLanguage(lang) ? lang : "en";
  const t = (key: MessageKey, vars?: Vars) => translate(uiLanguage, key, vars);
  return {
    language: lang,
    uiLanguage,
    locale: localeFor(lang, currency),
    t,
    rich: (key, nodes) => {
      const parts = translate(uiLanguage, key).split(/(\{\w+\})/g);
      return parts.map((part, i) => {
        const name = /^\{(\w+)\}$/.exec(part)?.[1];
        return <Fragment key={i}>{name && name in nodes ? nodes[name] : part}</Fragment>;
      });
    },
    // English keeps the app's house money format (identical to the staff app)
    money: (minor, code) => (lang === "en" ? fmtMoneyIn(code, minor) : formatMoneyLocale(minor, code, lang)),
    date: (iso, style = "medium") => formatDateLocale(iso, lang, { style, currency }),
  };
}

const I18nContext = createContext<I18n>(makeI18n("en"));

export const useI18n = () => useContext(I18nContext);

/**
 * Provides a language to everything below. Public pages wrap their content in
 * one keyed to the language the API served; `document` also sets <html lang>.
 */
export function I18nProvider({ language, currency, document: setDocumentLang, children }: { language: string | null | undefined; currency?: string | null; document?: boolean; children: ReactNode }) {
  const value = useMemo(() => makeI18n(language, currency), [language, currency]);
  useEffect(() => {
    if (!setDocumentLang) return;
    const prev = window.document.documentElement.lang;
    window.document.documentElement.lang = value.language;
    return () => {
      window.document.documentElement.lang = prev;
    };
  }, [setDocumentLang, value.language]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * The visitor's explicit language choice (?lang=), for public pages. The API
 * resolves ?lang= → known donor preference → Accept-Language → org default and
 * answers with the language served; the switcher just rewrites ?lang=.
 */
export function useRequestedLanguage(): { requested: LanguageCode | null; choose: (lang: LanguageCode) => void } {
  const [params, setParams] = useSearchParams();
  const requested = normalizeLanguage(params.get("lang"));
  return {
    requested,
    choose: (lang) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("lang", lang);
          return next;
        },
        { replace: true },
      ),
  };
}

/** appends ?lang= to an API path when the visitor chose one (otherwise the path is unchanged) */
export function withLang(path: string, lang: string | null | undefined): string {
  if (!lang) return path;
  return `${path}${path.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}`;
}

/** the language to record on a new constituent: only a non-default choice (the server ignores the default anyway) */
export function visitLanguage(info: { language?: LanguageCode; languages?: LanguageCode[] } | null | undefined): LanguageCode | undefined {
  const languages = info?.languages ?? [];
  return languages.length > 1 && info?.language && info.language !== languages[0] ? info.language : undefined;
}
